import React, { useEffect, useState } from 'react';
import { MessageSourceProps, withMessages } from 'react-message-source';
import axios from 'axios';
import { CountryDto, SupplierDto, SupplierSearchRequest, UpsertSupplierDto } from 'ts-types/api.types';
import { deleteSupplier, getSupplier, searchSuppliers } from 'services/suppliersService';
import VirtualizedTable from 'components/layout/VirtualizedTable';
import PraxStyledSegment from 'components/layout/PraxStyledSegment';
import PxGrid from 'components/layout/PxGrid';
import { Dimmer, Grid, Loader, Modal } from 'semantic-ui-react';
import DeleteActionButton from 'components/DeleteActionButton';
import EditActionButton from 'components/EditActionButton';
import AddButton from 'components/AddButton';
import FormModalContentContainer from 'components/layout/FormModalContentContainer';
import UpsertSupplierForm from 'components/settings/supplier/UpsertSuppliersForm';
import StyledErrorMessage from 'components/layout/StyledErrorMessage';
import StyledViewTableRow from 'components/layout/StyledViewTableRow';
import { Field, Form as FinalForm, FormSpy } from 'react-final-form';
import Input from 'components/final-form/Input';
import CheckBox from 'components/final-form/CheckBox';
import SearchResultComponent from 'components/SearchResultComponent';
import { debounce } from 'ts-debounce';
import { noop } from 'utils/functionUtils';
import { FormState } from 'final-form';
import { getAllCountries } from 'services/countryServices';
import _ from 'lodash';
import styled from 'styled-components';
import { errorUtils } from 'utils/errorUtils';

const cancelTokenSource = axios.CancelToken.source();

const SupplierContainer = styled(StyledViewTableRow)`

  .inactive-supplier {
    background-color: #e6e6e6;
  }

`;

interface SupplierFieldProps extends FormState {
  values: Partial<SupplierSearchRequest>
}

interface Props extends MessageSourceProps {

}

const SuppliersView = (props: Props) => {

  const {getMessage} = props;

  const [suppliers, setSuppliers] = useState<SupplierDto[]>([]);
  const [supplier, setSupplier] = useState<UpsertSupplierDto>();
  const [supplierId, setSupplierId] = useState<number>();
  const [addEditModalOpen, setAddEditModalOpen] = useState<boolean>(false);
  const [errorMessage, setErrorMessage] = useState<string | undefined>();
  const [suppliersLoaded, setSuppliersLoaded] = useState<boolean>(false);
  const [countriesMap, setCountriesMap] = useState<{ [key: number]: CountryDto }>([]);
  const [countries, setCountries] = useState<CountryDto[]>([]);

  const searchValues = {
    query: '',
    includeInactive: false
  }

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    setSuppliersLoaded(false);

    try {
      const fetchedCounties = await getAllCountries(cancelTokenSource);
      setCountriesMap((_.keyBy(fetchedCounties, 'id')));
      setCountries(fetchedCounties);
      const fetchedSuppliers = await searchSuppliers(searchValues, cancelTokenSource);

      setSuppliers(fetchedSuppliers);

    } finally {
      setSuppliersLoaded(true);
    }
  }

  const openAddEditModal = async (id: number | undefined) => {

    if (id) {

      setSupplierId(id);

      await getSupplier(id, cancelTokenSource)
          .then(response => {
            setSupplier(response);
          })
          .catch(e => handleError(e.response.data))
    }

    setAddEditModalOpen(true);
  }

  const onSaveUpsertForm = async () => {
    setSupplierId(undefined);
    setSupplier(undefined);

    const suppliers = await searchSuppliers(searchValues, cancelTokenSource);
    setSuppliers(suppliers);
    setAddEditModalOpen(false);
  }

  const hideModal = () => {
    setSupplierId(undefined);
    setSupplier(undefined);
    setAddEditModalOpen(false);
  }

  const onDeleteSupplier = async (id: number) => {
    const filteredSuppliers = suppliers.filter(supplier => supplier.id != id);

    await deleteSupplier(id, cancelTokenSource)
    setSuppliers(filteredSuppliers)
  }

  const handleError = (error: any) => {

    const errorCode = error.errorCode;
    const knownErrors: Array<String> = [
      errorUtils.supplierNotFound
    ];

    const violations: Array<any> = error.violations;

    if (violations && violations.length > 0) {

      violations.forEach(violation => {

        if (knownErrors.includes(violation.errorCode)) {
          setErrorMessage(getMessage(`error.${violation.errorCode}`))
        }
      });
    } else {
      if (knownErrors.includes(errorCode)) {
        setErrorMessage(getMessage(`error.${errorCode}`))
      } else {
        setErrorMessage(getMessage('error.general'));
      }
    }
  };

  const renderLoader = (): React.ReactNode => {
    return (
        <Dimmer active inverted>
          <Loader inline="centered" size="medium">{getMessage("supplier.loading")}</Loader>
        </Dimmer>
    );
  };

  const handleChange = debounce(({values}: SupplierFieldProps): void => {
    setSuppliersLoaded(false);

    const onFinally = () => {
      setSuppliersLoaded(true);
    };

    searchSuppliers(values, cancelTokenSource)
        .then(response => {
              setSuppliers(response)
            }
        )
        .catch(noop)
        .finally(onFinally);
  }, 300);


  const countryCellRenderer = ({rowData}: any) => {

    const country = countriesMap[rowData.countryId].name

    return (
        <div>
          {country}
        </div>
    )
  }

  const statusCellRenderer = ({rowData}: any) => {

    let active: string;

    if(rowData.active === true) {
      active = getMessage('supplier.status.active');
    } else {
      active = getMessage('supplier.status.inactive');
    }

    return (
        <div>
          {active}
        </div>
    )
  }

  const actionCellRenderer = ({rowData}: any) => {
    return (
        <>
          <EditActionButton
              hoverMessage={getMessage("msgEdit")}
              onConfirm={() => openAddEditModal(rowData.id)}
          />
          <DeleteActionButton
              hoverMessage={getMessage("msgDelete")}
              popupMessage={getMessage("supplier.confirmDelete")}
              onConfirm={() => onDeleteSupplier(rowData.id)}/>
        </>
    )
  }


  const renderSearchSuppliersSegment = (): JSX.Element => {

    return (
        <FinalForm
            onSubmit={() => {
            }}
            initialValues={searchValues}
            subscription={{values: true, pristine: true}}
            render={renderSearchForm}
        />
    );
  };


  const renderSearchForm = () => {

    const numResults = suppliers.length;

    return (
        <PxGrid>
          <Grid.Row>
            <Grid.Column width={3} verticalAlign="middle">
              {getMessage("select")}:
            </Grid.Column>
            <Grid.Column width={13}>
              <Field
                  name="query"
                  component={Input}
                  fluid
                  autoFocus
              />
            </Grid.Column>
          </Grid.Row>
          <Grid.Row>
            <Grid.Column width={3} verticalAlign="middle">
            </Grid.Column>
            <Grid.Column width={4} verticalAlign={"middle"}>
              <Field
                  name="includeInactive"
                  component={CheckBox}
                  label={getMessage("includingInactive")}
                  toggle={true}
              />
            </Grid.Column>
            <Grid.Column width={9} className="actions-row right">
              <AddButton buttonMessage={getMessage("button.add")} onClick={() => setAddEditModalOpen(true)}/>
            </Grid.Column>
          </Grid.Row>
          <SearchResultComponent numResults={numResults} textMessage={"search.resultsSummary"}/>
          <FormSpy subscription={{values: true}} onChange={(values) => handleChange(values)}/>
        </PxGrid>
    );
  }

  const suppliersRowGetter = ({index}: any) => {
    if (suppliers) {
      Object.assign(suppliers[index], {index: index + 1});
      return suppliers[index];
    }
  }

  const suppliersRowRenderer = ({className, columns, index, key, style}: any) => {
    const a11yProps = {'aria-rowindex': index + 1};

    const newClassName = suppliers[index].active ? className : className + ' inactive-supplier';

    return (
        <div
            {...a11yProps}
            className={newClassName}
            key={key}
            role={"row"}
            style={style}
            onDoubleClick={() => openAddEditModal(suppliers && suppliers[index].id)}
        >
          {columns}
        </div>
    )
  }

  const renderSuppliers = (): JSX.Element => {
    return (
        <VirtualizedTable
            rowCount={suppliers.length}
            rowGetter={suppliersRowGetter}
            rowRenderer={suppliersRowRenderer}
            rowHeight={42}
            columns={[
              {
                width: 100,
                label: "#",
                dataKey: "id"
              },
              {
                width: 300,
                label: (getMessage("supplier.clientNumber")),
                dataKey: "clientNumber"
              },
              {
                width: 300,
                flexGrow: 1,
                label: (getMessage("supplier.description")),
                dataKey: "description"
              },
              {
                width: 100,
                label: (getMessage("supplier.zip")),
                dataKey: "zip"
              },
              {
                width: 300,
                label: (getMessage("supplier.city")),
                dataKey: "city"
              },
              {
                width: 300,
                label: (getMessage("supplier.countryId")),
                dataKey: "countryId",
                cellRenderer: (countryCellRenderer)
              },
              {
                width: 300,
                label: (getMessage("supplier.email")),
                dataKey: "email"
              },
              {
                width: 300,
                label: (getMessage("supplier.tel")),
                dataKey: "tel"
              },
              {
                width: 300,
                label: (getMessage("supplier.status")),
                dataKey: "active",
                cellRenderer: (statusCellRenderer)
              },
              {
                width: 100,
                label: (getMessage("actions")),
                dataKey: "action",
                cellRenderer: (actionCellRenderer)
              }
            ]}
        />
    )
  }

  const renderSupplierModal = (): React.ReactNode => {
    return (
        <Modal
            open={addEditModalOpen}
            onClose={hideModal}
            closeOnDimmerClick={false}
        >
          <Modal.Header>{getMessage("header.navigation.supplier")}</Modal.Header>
          <FormModalContentContainer>
            <UpsertSupplierForm
                supplierId={supplierId}
                supplier={supplier}
                countries={countries}
                onCancel={hideModal}
                onSave={onSaveUpsertForm}
            />
          </FormModalContentContainer>
        </Modal>
    )
  }

  return (
      <PraxStyledSegment>
        {errorMessage
            && (
                <StyledErrorMessage onDismiss={() => setErrorMessage(undefined)}>
                  {errorMessage}
                </StyledErrorMessage>
            )
        }

        <div>
          {renderSearchSuppliersSegment()}
        </div>

        <SupplierContainer>
          {!suppliersLoaded ? renderLoader() : renderSuppliers()}
        </SupplierContainer>

        {renderSupplierModal()}
      </PraxStyledSegment>
  )
}

export default withMessages(SuppliersView);
